import type { IModelItem } from '@ardoq/yfiles';
import { componentInterface } from '@ardoq/component-interface';
import { isTruthy } from '@ardoq/common-helpers';
import {
  formatOtherLabel,
  GraphGroup,
  GraphItem,
  isCollapsibleGroup,
  isComponentGraphEdgeModel,
  ItemLabels,
} from '@ardoq/graph';
import { dataModelId } from './graphComponentUtil';
import { Edge } from 'graph/edge';
import {
  BLOCK_DIAGRAM_EDGE_LABEL_FONT,
  BLOCK_DIAGRAM_LABEL_TEXT_PARAMS,
  BLOCK_DIAGRAM_NODE_LABEL_FONT,
  BLOCK_DIAGRAM_OTHER_LABEL_FONT,
  BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_FONT,
  MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS,
  MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_FONT,
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE,
} from 'yfilesExtensions/styles/consts';
import {
  BLOCK_DIAGRAM_GROUP_LABEL_FONT,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_LABEL_FONT,
} from 'tabview/blockDiagram/view/consts';
import { measureGroupSubLabel } from 'tabview/blockDiagram/view/utils';
import {
  getTextHeightWithinBlock,
  LEGACY_FORMATTING_VALUE_HEIGHT_NODE,
  measureExpandedGroupNodeLabelWidth,
  measureExpandedGroupNodeLabelWidthModernized,
} from 'tabview/blockDiagram/view/yFilesExtensions/labels/legacyLabelUtils';
import { getMaxGroupLabelHeight } from 'tabview/blockDiagram/view/yFilesExtensions/GroupBoundsCalculator';

const getLegacyLabelsAsData = (graphItem: GraphItem): ItemLabels => {
  const modelId = dataModelId(graphItem);

  const mainLabel = componentInterface.isComponent(modelId)
    ? componentInterface.getNameWithFieldLabelAndValue(modelId)
    : graphItem.getLabel();

  return {
    mainLabel,
    subLabel: graphItem instanceof GraphGroup ? graphItem.subLabel : undefined,
  };
};

const graphItemTooltipText = (
  labelOwner: IModelItem,
  labelWidth: number,
  isModern: boolean
) => {
  const { tag } = labelOwner;
  const graphItem = tag as GraphItem | null;
  if (!graphItem) {
    return null;
  }

  const isGroup = isCollapsibleGroup(graphItem);
  const isCollapsed = isGroup && graphItem.collapsed;
  const isEdge =
    graphItem instanceof Edge || isComponentGraphEdgeModel(graphItem);

  const mainLabelFont = isEdge
    ? isModern
      ? MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_FONT
      : BLOCK_DIAGRAM_EDGE_LABEL_FONT
    : isGroup && !isCollapsed
      ? isModern
        ? MODERNIZED_BLOCK_DIAGRAM_GROUP_LABEL_FONT
        : BLOCK_DIAGRAM_GROUP_LABEL_FONT
      : isModern
        ? MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_FONT
        : BLOCK_DIAGRAM_NODE_LABEL_FONT;

  const itemLabels =
    graphItem.getItemLabels?.() ?? getLegacyLabelsAsData(graphItem);

  if (!itemLabels) {
    return null;
  }

  const subLabelText = itemLabels.subLabel ? `\n\n${itemLabels.subLabel}` : '';

  const {
    mainLabel,
    legacyLabelParts,
    legacyTruncatedFieldValueAndLabelForNode,
  } = itemLabels;

  const otherLabelsTexts = (itemLabels.otherLabels || []).map(
    otherLabel => `[${formatOtherLabel(otherLabel)}]`
  );

  const legacyFormattingText = legacyLabelParts?.fieldValue
    ? `[${legacyLabelParts.fieldLabel}: ${legacyLabelParts.fieldValue}]`
    : null;

  const legacyFormattingSpace = legacyFormattingText
    ? LEGACY_FORMATTING_VALUE_HEIGHT_NODE
    : 0;

  const maxNodeLabelSize = isModern
    ? MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS.maximumSize
    : BLOCK_DIAGRAM_LABEL_TEXT_PARAMS.maximumSize;

  const maxLabelHeight = isGroup
    ? getMaxGroupLabelHeight(Boolean(legacyFormattingText), isModern)
    : maxNodeLabelSize.height - legacyFormattingSpace;

  const isMainLabelTruncated = mainLabel
    ? getTextHeightWithinBlock(mainLabel, mainLabelFont, isModern) >
      maxLabelHeight
    : false;

  const isSublabelTruncated =
    // that's not really correct. The grop width is dynamic.
    measureGroupSubLabel(subLabelText) > labelWidth;

  const isOtherLabelTruncated = otherLabelsTexts.some(
    text =>
      getTextHeightWithinBlock(
        text,
        isModern
          ? MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT
          : BLOCK_DIAGRAM_OTHER_LABEL_FONT,
        isModern
      ) >
      (isModern
        ? MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE.height
        : BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE.height)
  );

  const isLegacyFormattingTruncated =
    legacyFormattingText &&
    (isGroup
      ? (isModern
          ? measureExpandedGroupNodeLabelWidthModernized(legacyFormattingText)
          : measureExpandedGroupNodeLabelWidth(legacyFormattingText)) >
        labelWidth
      : legacyFormattingText !== legacyTruncatedFieldValueAndLabelForNode);

  if (
    isMainLabelTruncated ||
    isSublabelTruncated ||
    isOtherLabelTruncated ||
    isLegacyFormattingTruncated
  ) {
    return `${[mainLabel, ...otherLabelsTexts, legacyFormattingText].filter(isTruthy).join('\n')}${subLabelText}`;
  }
  return null;
};

export { graphItemTooltipText };
